import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

//Material imports
import Grid from "@mui/material/Grid";
import makeStyles from "@mui/styles/makeStyles";

//Limitless imports
import Typography from "@limitless/modern/data-display/typography/Typography";
import { BoxRenderer } from "@limitless/portal";
import DataTable from "@limitless/modern/data-display/table/DataTable.js";
import StarRatingSystem from "@limitless/modern/components/star-ratings/StarRatings";
import { commonRatingFilter } from "./Filters";
import { SimpleAccordion } from "@limitless/portal";
import { aiStar } from "@limitless/images";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import { useAppConfig } from "@limitless/common/hooks";
import Link from "@mui/material/Link";
import { useTranslation } from "@limitless/common/hooks";

const renderValueCell = (score, schemeType) => {
    return (
        <Grid display={"flex"} justifyContent="center">
            {schemeType === "5-stars" ? (
                <StarRatingSystem id="message-star-ratings" maxScore={5} defaultScore={0} rating={score} disabled />
            ) : schemeType === "binary" ? (
                <BoxRenderer
                    showRadius
                    color={parseInt(score) === 0 ? "red" : "secondary-main"}
                    value={parseInt(score) === 0 ? "No" : parseInt(score) === 1 ? "Yes" : "Not answered"}
                    variant="caption"
                    containerStyles={{ padding: "4px 12px" }}
                    noMargin
                    centerTextAlign
                    id="scheme"
                />
            ) : (
                score
            )}
        </Grid>
    );
};

export const renderRatingScore = (title, reviewScore, showAiIcon = false, classes) => {
    return (
        <Grid>
            {showAiIcon && <AiReviewIcon className={classes.iconImgAi} />}
            <Typography variant="caption">{`${title} `}</Typography>
            <Typography
                variant="caption"
                color={theme => (reviewScore >= 50 ? theme.palette.primary.dark : theme.palette.error.dark)}
            >{`${reviewScore}%`}</Typography>
        </Grid>
    );
};

const useStyles = makeStyles(theme => ({
    accordionStyling: {
        width: "100%",
        backgroundImage: "none",
        background: "transperant",
        boxShadow: "none !important"
    },
    icon: {
        paddingTop: "8px",
        width: 20,
        height: 20,
        color: theme.palette.primary.main
    },
    qrContactUs: {
        display: "inline",
        cursor: "pointer",
        color: theme.palette.primary.main,
        textDecoration: "none"
    },
    iconImg: {
        width: 20,
        height: 20,
        marginBottom: -15,
        marginLeft: -5
    },
    iconImgAi: {
        width: 20,
        height: 20,
        marginRight: 10
    }
}));

const AiReviewIcon = ({ className }) => (
    <IconButton
        icon={<img src={aiStar} alt="Review performed by an AI Expert" className={className} />}
        label="This review was performed by an AI Expert"
    />
);

AiReviewIcon.propTypes = {
    className: PropTypes.string.isRequired
};

const RatingMessageDetails = ({ message, isGenaiReviewMessage }) => {
    const classes = { ...commonRatingFilter(), ...useStyles() };
    const showAiIcon = isGenaiReviewMessage[0]?.isGenaiReview;
    const { getContactUsUrl } = useAppConfig();
    const history = useHistory();
    const { t } = useTranslation();

    const handleClick = messageId => {
        const navigationUrl = "/contactusscreen";
        const url = getContactUsUrl(navigationUrl);
        if (url === navigationUrl) {
            history.push({
                pathname: url,
                state: { messageId, type: "messageId" }
            });
        } else {
            window.open(url, "_blank");
        }
    };

    const renderAccordion = (criteriaTitle, criteriaChildren) => {
        return (
            <Grid sx={{ width: "280px", display: "flex" }}>
                {!criteriaChildren ? (
                    <Typography variant="body2">{criteriaTitle}</Typography>
                ) : (
                    <SimpleAccordion
                        key={"QualityRatingCriteria"}
                        variant="body2"
                        descColor="initial"
                        rootStyle={classes.summaryRoot}
                        classes={classes}
                        handleAccordionClick={null}
                        title={criteriaTitle}
                    >
                        {criteriaChildren}
                    </SimpleAccordion>
                )}
            </Grid>
        );
    };

    const column = [
        {
            name: "description",
            label: "description",
            options: {
                display: false
            }
        },
        {
            name: "name",
            label: "Criteria",
            options: {
                width: "100px",
                customBodyRender: (v, tableMeta) => renderAccordion(v, tableMeta.rowData[0])
            }
        },
        {
            name: "weight",
            label: "Weighting",
            options: {
                customBodyRender: weight => (weight > 0 ? `${weight}%` : "n/a"),
                setCellProps: () => ({ style: { textAlign: "center" } }),
                setCellHeaderProps: () => {
                    return { align: "center" };
                }
            }
        },
        {
            name: "reviewScheme",
            label: "reviewScheme",
            options: {
                display: false
            }
        },
        {
            name: "score",
            label: "Rating",
            options: {
                customBodyRender: (v, tableMeta) => renderValueCell(v, tableMeta.rowData[3]),
                setCellHeaderProps: () => {
                    return { align: "center" };
                },
                setCellProps: () => ({ width: "200px" })
            }
        }
    ];

    return (
        <Grid container>
            {message.reviewScore > 0 && (
                <Grid>
                    {message.includedInRating && message.ratingType?.includes("review") ? (
                        <>
                            {showAiIcon && <AiReviewIcon className={classes.iconImg} />}
                            <BoxRenderer
                                fontSize={12}
                                fontWeight={400}
                                value={`Quality rating: ${message.reviewScore}%`}
                                color={
                                    message.reviewScore >= 80
                                        ? "secondary-main"
                                        : message.reviewScore >= 50
                                        ? "warning-light"
                                        : "error-light"
                                }
                                variant="caption"
                                showRadius
                                noMargin
                                addPadding
                                centerTextAlign
                                containerStyles={{ padding: "4px 8px" }}
                            />
                        </>
                    ) : (
                        renderRatingScore("Quality rating:", message.reviewScore, showAiIcon, classes)
                    )}

                    <Typography variant="body2" mt={3}>
                        Here you can see how you were reviewed against different criteria
                        {isGenaiReviewMessage[0]?.isGenaiReview ? (
                            <>
                                {t(", If you think this is incorrect, please")}{" "}
                                <Typography
                                    variant="body2"
                                    className={classes.qrContactUs}
                                    component={Link}
                                    onClick={() => handleClick(message.messageId)}
                                    display="inline"
                                >
                                    contact us.
                                </Typography>
                            </>
                        ) : (
                            ":"
                        )}
                    </Typography>

                    <Typography variant="body2" mt={2}>
                        Feedback
                    </Typography>
                    <Typography className={classes.italicTypography}>
                        {message.reviewNotes && message.reviewNotes[0].note}
                    </Typography>
                    {message?.reviewCriteria?.length > 0 && (
                        <Grid mt={2}>
                            <DataTable
                                columns={column}
                                data={message?.reviewCriteria}
                                option={{ sort: false, fixedHeader: true, tableBodyMaxHeight: "390px" }}
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </Grid>
    );
};

RatingMessageDetails.propTypes = {
    message: PropTypes.object,
    isGenaiReviewMessage: PropTypes.any
};

export default RatingMessageDetails;
